import { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Button, IconButton, Link as MuiLink, Tooltip, Typography } from '@mui/material';
import { AccountBox, Edit, LocationOn, Mail, Person, Phone } from '@mui/icons-material';
import { Card } from '../../users/details/Card';
import { InfoRow, formatAddress, formatPhone } from '../../users/details/InfoRow';
import { SetEmailDialog } from '../../components/SetEmailDialog';
import { GET_CONTACT, UPDATE_CONTACT, CANCEL_UPDATE_CONTACT } from './ContactPerson.graphql';
import LoadingSpinner from '../../components/LoadingSpinner';
import { LicenseModel, User } from '../../interfaces';
import { useMutation, useQuery } from '@apollo/client/react';

interface Props {
  licenseId: number;
}

interface ContactData {
  licenseContact: {
    license: LicenseModel;
    bksLicense: { contact: User | null };
  };
}

export function ContactPerson({ licenseId }: Props) {
  const [openChangeDialog, setOpenChangeDialog] = useState(false);
  const [cancelUpdate, { loading: cancelling }] = useMutation<any, any>(CANCEL_UPDATE_CONTACT);
  const [updateContact] = useMutation<any, any>(UPDATE_CONTACT);
  const { loading, data } = useQuery<ContactData>(GET_CONTACT, { variables: { id: licenseId } });

  // Line up with the MainContentPanels around it on the licence page.
  const cardSx = { mt: 2, mr: 2 };

  if (loading || !data)
    return (
      <Card title="Administrator" sx={cardSx}>
        <LoadingSpinner />
      </Card>
    );

  const { license, bksLicense } = data.licenseContact;
  const contact = bksLicense.contact;
  const hasPendingChange = !!license?.pendingContact;

  return (
    <Card
      title="Administrator"
      sx={cardSx}
      action={
        !hasPendingChange && (
          <Tooltip title="Bytt administrator">
            <IconButton size="small" onClick={() => setOpenChangeDialog(true)}>
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )
      }>
      {hasPendingChange && (
        <Alert
          severity="info"
          sx={{ mx: 1.5, mt: 0.5, mb: 1 }}
          action={
            <Button
              color="inherit"
              size="small"
              loading={cancelling}
              sx={{ whiteSpace: 'nowrap' }}
              onClick={() => cancelUpdate({ variables: { input: { id: licenseId, userId: license.pendingContact } } })}>
              Avbryt bytte
            </Button>
          }>
          Venter på at{' '}
          <MuiLink component={RouterLink} to={`/user/${license.pendingContact}`} color="inherit">
            den nye administratoren
          </MuiLink>{' '}
          bekrefter byttet.
        </Alert>
      )}

      {contact ? (
        <>
          <InfoRow icon={<AccountBox />} label="Brukernavn" value={contact.loginId} />
          <InfoRow
            icon={<Person />}
            label="Navn"
            value={contact.displayName || `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim()}
            valueRender={(v) => (
              <MuiLink component={RouterLink} to={`/user/${contact.id}`} underline="hover">
                {v}
              </MuiLink>
            )}
          />
          <InfoRow
            icon={<Mail />}
            label="E-post"
            value={contact.email}
            valueRender={(v) => (
              <MuiLink href={`mailto:${v}`} underline="hover">
                {v}
              </MuiLink>
            )}
          />
          <InfoRow icon={<Phone />} label="Telefon" value={formatPhone(contact.phone)} />
          <InfoRow icon={<LocationOn />} label="Adresse" value={formatAddress(contact.address)} />
        </>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1.25 }}>
          Ingen administrator
        </Typography>
      )}

      <SetEmailDialog
        open={openChangeDialog}
        title="Bytt administrator"
        description="Den nye administratoren får en e-post for å bekrefte byttet. Til det er bekreftet, vises lisensen som «I påvente av bytte». Finnes det ingen bruker med e-postadressen, blir den opprettet."
        submitLabel="Bytt administrator"
        onClose={() => setOpenChangeDialog(false)}
        update={(email: string) => updateContact({ variables: { input: { id: licenseId, email: email } } })}
      />
    </Card>
  );
}
